import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * The SAPHIR design system on top of Aura.
 *
 * Green (`#54b083`) is the one brand accent: primary buttons, active states,
 * focus rings. Everything structural stays achromatic with a faint green cast
 * — the neutral ramps below are the design system's "stone" scale, not Aura's
 * blue-tinted slate. Elevation is border + subtle shadow; surfaces themselves
 * barely change.
 *
 * The raw values live in `styles.scss` as `--sw-*` custom properties for the
 * shell's own chrome; this preset feeds the same palette to every PrimeNG
 * component, so modules built on PrimeNG pick the design up without knowing
 * about it.
 */
export const SaphirPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
  },
  semantic: {
    primary: {
      50: '#f2faf6',
      100: '#e6f5ee',
      200: '#c8ead9',
      300: '#a3dbc0',
      400: '#7cc6a2',
      500: '#54b083',
      600: '#3d9068',
      700: '#2a7050',
      800: '#1e5138',
      900: '#143626',
      950: '#0a1a12',
    },
    colorScheme: {
      light: {
        primary: {
          color: '#54b083',
          contrastColor: '#ffffff',
          hoverColor: '#3d9068',
          activeColor: '#2a7050',
        },
        highlight: {
          background: '#e6f5ee',
          focusBackground: '#c8ead9',
          color: '#2a7050',
          focusColor: '#143626',
        },
        surface: {
          0: '#ffffff',
          50: '#f8fbf9',
          100: '#f3f7f5',
          200: '#edf3ef',
          300: '#dce6e2',
          400: '#a4b8b0',
          500: '#7a9287',
          600: '#556b5e',
          700: '#2c3d35',
          800: '#1e2b25',
          900: '#161e1a',
          950: '#0f1512',
        },
      },
      dark: {
        primary: {
          color: '#54b083',
          contrastColor: '#0a1a12',
          hoverColor: '#7cc6a2',
          activeColor: '#a3dbc0',
        },
        highlight: {
          background: 'rgba(84, 176, 131, 0.18)',
          focusBackground: 'rgba(84, 176, 131, 0.28)',
          color: 'rgba(255, 255, 255, 0.87)',
          focusColor: 'rgba(255, 255, 255, 0.87)',
        },
        surface: {
          0: '#ffffff',
          50: '#f3f7f5',
          100: '#dce6e2',
          200: '#b9c9c2',
          300: '#7a9287',
          400: '#556b5e',
          500: '#3d5148',
          600: '#2c3d35',
          700: '#22312a',
          800: '#182420',
          900: '#121b17',
          950: '#0c1310',
        },
      },
    },
  },
});
